import React, { useRef, useMemo, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { nodeColors } from '../../../../utils/neo4j';

export default function GraphView({ triples, allTriples, onNodeClick, selectedNode }) {
  const fgRef = useRef();

  // Use allTriples for color mapping if provided, otherwise use triples
  const triplesForColorMapping = allTriples || triples;

  // Build graphData from triples
  const graphData = useMemo(() => {
    const data = { nodes: [], links: [] };
    const nodeMap = new Map();

    triples.forEach(t => {
      if (!nodeMap.has(t.subject.id)) {
        nodeMap.set(t.subject.id, {
          id: t.subject.id,
          name: t?.subject?.properties?.searchName ||
            t?.subject?.properties?.name ||
            t?.subject?.properties?.title ||
            t.subject.type,
          group: t.subject.type
        });
      }

      if (!nodeMap.has(t.object.id)) {
        nodeMap.set(t.object.id, {
          id: t.object.id,
          name: t?.object?.properties?.searchName ||
            t?.object?.properties?.name ||
            t.object.type,
          group: t.object.type
        });
      }

      data.links.push({
        source: t.subject.id,
        target: t.object.id,
        label: t.predicate.type
      });
    });

    data.nodes = Array.from(nodeMap.values());

    // ---- Assign colors per group based on ALL triples (not filtered) ----
    // Extract unique groups from all triples to ensure consistent colors
    const allGroups = new Set();
    triplesForColorMapping.forEach(t => {
      allGroups.add(t.subject.type);
      allGroups.add(t.object.type);
    });
    const uniqueGroups = [...allGroups];

    uniqueGroups.forEach((g, i) => {
      const color = nodeColors[i % nodeColors.length];
      data.nodes
        .filter(n => n.group === g)
        .forEach(n => {
          n.color = color.background;
          n.textColor = color.textColor;
        });
    });

    return data;
  }, [triples, triplesForColorMapping]);

  // Zoom to fit when graph data changes (filter applied)
  useEffect(() => {
    if (fgRef.current && graphData.nodes.length > 0) {
      // Use setTimeout to ensure the graph has rendered
      setTimeout(() => {
        fgRef.current.zoomToFit(1000, 250);
      }, 100);
    }
  }, [graphData]);

  return (
    <ForceGraph2D
      ref={fgRef}
      graphData={graphData}
      backgroundColor="#fafafa"
      nodeAutoColorBy={null}   // we set colors ourselves
      linkDirectionalArrowLength={2}
      linkDirectionalArrowRelPos={10}
      linkCurvature={0.3}
      d3VelocityDecay={0.3}
      d3AlphaDecay={0.02}
      d3Force={{
        charge: -300,
        link: { distance: 300 },
      }}
      onBackgroundClick={() => {
        if (selectedNode) {
          onNodeClick(null)
        }
      }}
      nodePointerAreaPaint={(node, color, ctx) => {
        // Use a fixed radius for hit detection to ensure consistency
        const isSelected = selectedNode && selectedNode.id === node.id;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(node.x, node.y, isSelected ? 10 : 8, 0, 2 * Math.PI, false);
        ctx.fill();
      }}
      nodeLabel={node => `Type: ${node.group}<br/>Name: ${node.name}`}
      onNodeClick={onNodeClick}

      // --- Custom Node Render ---
      nodeCanvasObject={(node, ctx, globalScale) => {
        const label = node.name;
        const fontSize = Math.max(2, 5 / globalScale);
        const isSelected = selectedNode && selectedNode.id === node.id;
        const radius = isSelected ? 10 : 8;

        const bgcolor = node.color || '#59bfb9';
        // Circle fill
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
        ctx.fillStyle = bgcolor;
        ctx.fill();

        // --- Stroke layers ---
        // Base white stroke
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = isSelected ? 2 : 1;
        ctx.stroke();

        // Highlight / default border
        ctx.strokeStyle = isSelected ? `${bgcolor}77` : 'rgba(207,200,200,0.4)';
        ctx.lineWidth = isSelected ? 1.5 : 0.5;
        ctx.stroke();

        // --- Label ---
        if (globalScale > 3) {
          ctx.font = `${fontSize}px Sans-Serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = node.textColor || '#4f4f4f';

          const maxWidth = radius * 1.8;
          const textWidth = ctx.measureText(label).width;

          if (textWidth > maxWidth) {
            let truncated = label;
            let truncatedWidth = textWidth;

            while (truncatedWidth > maxWidth && truncated.length > 0) {
              truncated = truncated.slice(0, -1);
              truncatedWidth = ctx.measureText(truncated + '...').width;
            }

            ctx.fillText(truncated + '...', node.x, node.y);
          } else {
            ctx.fillText(label, node.x, node.y);
          }
        }
      }}

      // --- Custom Link Render (arrow + label) ---
      linkCanvasObjectMode={() => 'after'}
      linkCanvasObject={(link, ctx, globalScale) => {
        if (globalScale <= 3) return;

        const fontSize = Math.max(2, 5 / globalScale);
        if (fontSize < 1) return;

        const start = link.source;
        const end = link.target;
        const midX = (start.x + end.x) / 2;
        const midY = (start.y + end.y) / 2;

        // Label background
        ctx.font = `${fontSize}px Sans-Serif`;
        const textWidth = ctx.measureText(link.label).width;
        const padding = 2;

        ctx.fillStyle = '#f5f7f766';
        ctx.fillRect(
          midX - textWidth / 2 - padding,
          midY - fontSize / 2 - padding,
          textWidth + padding * 2,
          fontSize + padding * 2
        );

        // Label text
        ctx.fillStyle = '#555';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(link.label, midX, midY);
      }}
    />
  );
}